import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, SearchX, X } from "lucide-react";
import { getCategories, getFaqs } from "@/lib/api";
import { SearchBar } from "@/components/SearchBar";
import { FaqCard } from "@/components/FaqCard";

const pill = (active) =>
  `shrink-0 whitespace-nowrap font-mono text-xs uppercase tracking-wider px-3 h-9 rounded-sm border transition-colors duration-200 active:scale-95 ${active ? "bg-amber-500 border-amber-500 text-[#0B0D10]" : "border-[#27303E] bg-[#0B0D10] text-slate-300 hover:border-amber-500"}`;

export default function Library() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const pillsRef = useRef(null);
  const { data: cats = [] } = useQuery({ queryKey: ["categories"], queryFn: getCategories });
  const { data: faqs = [], isLoading } = useQuery({
    queryKey: ["faqs", category, q],
    queryFn: () => getFaqs({ category: category || undefined, q: q || undefined }),
  });

  useEffect(() => {
    const box = pillsRef.current;
    const el = box?.querySelector('[data-active="true"]');
    if (el) box.scrollTo({ left: el.offsetLeft - box.clientWidth / 2 + el.clientWidth / 2, behavior: "smooth" });
  }, [category, cats.length]);

  const update = (k, v) => {
    const p = new URLSearchParams(params);
    v ? p.set(k, v) : p.delete(k);
    setParams(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const current = cats.find((c) => c.slug === category);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14" data-testid="library-page">
      <p className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-amber-500">FAQ Library</p>
      <h1 className="font-display text-[2.2rem] sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-none mt-2">
        {current ? current.name : "All repair questions"}
      </h1>
      <div className="sticky top-14 md:top-16 z-30 -mx-4 px-4 sm:mx-0 sm:px-0 py-3 mt-4 sm:mt-6 bg-[#0B0D10]/95 backdrop-blur-xl md:static md:bg-transparent md:backdrop-blur-none" data-testid="library-filter-bar">
        <div className="max-w-2xl"><SearchBar key={q} initial={q} size="sm" onSearch={(v) => update("q", v.trim())} /></div>
        <div ref={pillsRef} className="flex gap-2 overflow-x-auto no-scrollbar mt-3 sm:mt-5 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="category-filters">
          <button data-active={!category} className={pill(!category)} onClick={() => update("category", "")} data-testid="filter-category-all">All</button>
          {cats.map((c) => (
            <button key={c.slug} data-active={category === c.slug} className={pill(category === c.slug)} onClick={() => update("category", c.slug)} data-testid={`filter-category-${c.slug}`}>
              {c.name} <span className="opacity-60">({c.count})</span>
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3 mt-3 sm:mt-6 mb-4 sm:mb-8 font-mono text-xs uppercase tracking-widest text-slate-400">
        <span data-testid="results-count">{faqs.length} results</span>
        {q && (
          <button onClick={() => update("q", "")} data-testid="clear-search-btn" className="inline-flex items-center gap-1 border border-[#27303E] px-2.5 py-1.5 rounded-sm hover:border-amber-500 text-slate-300">
            "{q}" <X size={12} />
          </button>
        )}
      </div>
      {isLoading ? (
        <Loader2 className="animate-spin text-amber-500" data-testid="faqs-loading" />
      ) : faqs.length === 0 ? (
        <div className="border border-dashed border-[#27303E] p-8 sm:p-14 text-center rounded-sm" data-testid="no-results">
          <SearchX className="mx-auto text-slate-500" size={36} />
          <p className="font-display text-2xl uppercase font-bold mt-4">No matching answers</p>
          <p className="text-slate-400 text-sm mt-2">Try different words, or ask the AI Mechanic for a custom diagnosis.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-3 sm:gap-5" data-testid="faq-results">
          {faqs.map((f, i) => <FaqCard key={f.slug} faq={f} index={i} />)}
        </div>
      )}
    </main>
  );
}
